import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { Pagination } from "components/Pagination/Pagination";
import { labelCursado } from "components/EspaciosTable/EspaciosTable";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { AreaI } from "interfaces/Area";
import { CicloI } from "interfaces/Ciclo";
import { EspacioCurricularI } from "interfaces/EspacioCurricular";
import { NivelI } from "interfaces/Nivel";
import { PlanEstudioI } from "interfaces/PlanEstudio";
import { TipoEspacioI } from "interfaces/TipoEspacio";
import { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { createCajaCurricularSchema } from "schemas/cajaCurricular/CreateCajaCurricularSchema";
import { getArea } from "services/area";
import {
  getCajaCurricularById,
  postCajaCurricular,
  putCajaCurricular,
} from "services/cajaCurricular";
import { getCiclo } from "services/ciclo";
import { getEspaciosCurriculares } from "services/espacioCurricular";
import { getNiveles } from "services/niveles";
import { getPlanesEstudio } from "services/planesEstudio";
import { getTipoEspacio } from "services/tipoEspacio";

const FILTROS_INICIALES = {
  nombre: "",
  descripcion: "",
  area_id: "",
  nivel_id: "",
  ciclo_id: "",
  cuatrimestre: "",
  tipo_espacio_id: "",
  sub_tipo: "",
};

const CURSADO_FILTRO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "anual", label: "Anual" },
  { value: "1", label: "1er cuatrimestre" },
  { value: "2", label: "2do cuatrimestre" },
];

export const CajasCurricularesABM = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [defaultPlanes, setDefaultPlanes] = useState<PlanEstudioI[] | null>(
    null,
  );

  // Listado de espacios
  const [isLoadingEspacios, setIsLoadingEspacios] = useState(false);
  const [espacios, setEspacios] = useState<EspacioCurricularI[]>([]);
  const [cantidad, setCantidad] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT_PAGINATION);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_INICIALES);
  // Los AsyncSelect guardan su propio estado: cambiar la key los resetea
  const [filtrosKey, setFiltrosKey] = useState(0);

  const handleReturn = () => navigate(PRIVATE_ROUTE.CAJAS_CURRICULARES);

  const handleSubmitForm = async (params: any) => {
    try {
      if (isEditMode) {
        await putCajaCurricular({ params });
        showSuccessToast("¡La caja curricular fue editada con éxito!");
      } else {
        await postCajaCurricular({ params });
        showSuccessToast("¡La caja curricular fue creada con éxito!");
      }
      handleReturn();
    } catch (err: any) {
      showErrorToast(
        isEditMode
          ? "La caja curricular no pudo ser editada."
          : "La caja curricular no pudo ser creada.",
      );
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      id: "" as string | number,
      nombre: "",
      arr_planes_estudio_id: [] as (number | string)[],
      arr_espacios_id: [] as (number | string)[],
    },
    validationSchema: createCajaCurricularSchema,
    onSubmit: handleSubmitForm,
  });

  const filtros = useFormik({
    initialValues: FILTROS_INICIALES,
    onSubmit: (filtrosForm) => {
      setFiltrosAplicados(filtrosForm);
      searchEspacios(filtrosForm, 1, limit);
    },
  });

  const searchEspacios = async (
    filtrosBusqueda: typeof FILTROS_INICIALES,
    newPage: number,
    newLimit: number,
  ) => {
    try {
      setIsLoadingEspacios(true);
      const response = await getEspaciosCurriculares({
        ...filtrosBusqueda,
        page: newPage,
        limit: newLimit,
      });
      setEspacios(response.elementos);
      setCantidad(response.cantidad);
      setPage(newPage);
      setLimit(newLimit);
    } catch (err) {
      showErrorToast("No fue posible cargar los espacios curriculares");
    } finally {
      setIsLoadingEspacios(false);
    }
  };

  const handleClearFilters = () => {
    filtros.resetForm();
    setFiltrosKey((k) => k + 1);
    setFiltrosAplicados(FILTROS_INICIALES);
    searchEspacios(FILTROS_INICIALES, 1, limit);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchEspacios(
      filtrosAplicados,
      Number(newFiltros.page ?? page),
      Number(newFiltros.limit ?? limit),
    );
  };

  const isSelected = (espacioId: number | string) =>
    values.arr_espacios_id.some((ctId) => ctId == espacioId);

  const toggleEspacio = (espacioId: number | string) => {
    setFieldValue(
      "arr_espacios_id",
      isSelected(espacioId)
        ? values.arr_espacios_id.filter((ctId) => ctId != espacioId)
        : [...values.arr_espacios_id, espacioId],
    );
  };

  const isPageSelected =
    espacios.length > 0 && espacios.every((ctEl) => isSelected(ctEl.id));

  const togglePage = () => {
    const pageIds = espacios.map((ctEl) => ctEl.id);
    setFieldValue(
      "arr_espacios_id",
      isPageSelected
        ? values.arr_espacios_id.filter(
            (ctId) => !pageIds.some((pageId) => pageId == ctId),
          )
        : [
            ...values.arr_espacios_id,
            ...pageIds.filter((pageId) => !isSelected(pageId)),
          ],
    );
  };

  const loadEditForm = async () => {
    try {
      const caja = await getCajaCurricularById(id);
      if (!caja) throw new Error("Caja curricular no encontrada");
      setValues({
        id: caja.id,
        nombre: caja.nombre,
        arr_planes_estudio_id: caja.planesEstudio.map((pe) => pe.id),
        arr_espacios_id: caja.espaciosCurriculares.map((ec) => ec.id),
      });
      setDefaultPlanes(caja.planesEstudio);
    } catch (err) {
      showErrorToast("No fue posible cargar la caja curricular a editar");
      handleReturn();
    }
  };

  useEffect(() => {
    if (isEditMode) loadEditForm();
    searchEspacios(FILTROS_INICIALES, 1, limit);
  }, [id]);

  const asyncFilterProps = {
    onChange: filtros.handleChange,
    values: filtros.values,
    variant: "gray-container" as const,
    paramKey: "nombre",
    arrElementsPath: "elementos",
    noOptionsMessage: "No hay opciones disponibles para el nombre ingresado",
    emptySelectMessage: "Ingresar un nombre",
  };

  return (
    <section>
      <h1 className="section-title">
        {isEditMode ? "Editar caja curricular" : "Nueva caja curricular"}
      </h1>
      <h2 className="section-subtitle mt-2">
        Configurá los datos de la caja curricular y seleccioná los espacios
        curriculares.
      </h2>

      <div>
        <Card className="mb-3">
          <Row>
            <Col sm={12} md={6} className="mb-2">
              <Input
                id={"nombre"}
                title="Nombre de la caja curricular"
                required
                onChange={handleChange}
                onBlur={handleBlur}
                values={values}
                errors={errors}
                touched={touched}
                variant="gray-container"
                size="lg"
              />
            </Col>
            <Col sm={12} md={6} className="mb-2">
              <AsyncSelect
                onChange={handleChange}
                id="arr_planes_estudio_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de plan de estudio"}
                size="lg"
                isMulti={true}
                title="Planes de estudio"
                getter={(params) => getPlanesEstudio({ ...params, limit: 50 })}
                paramKey={"nombre"}
                variant="gray-container"
                arrElementsPath="elementos"
                defaultSelectedOptions={defaultPlanes}
                optionsFormatConfig={{
                  value: "id",
                  label: (el: PlanEstudioI) => `${el.nombre}`,
                }}
                required
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
          </Row>
        </Card>

        <Row>
          <Col lg={4} xl={3} className="mb-3">
            <Card as="form" onSubmit={(e: any) => filtros.handleSubmit(e)}>
              <h5 className="mb-3">Filtros de búsqueda</h5>
              <div key={filtrosKey} className="d-flex flex-column gap-3">
                <Input
                  id="nombre"
                  title="Nombre de la materia"
                  placeholder="Buscar por nombre de materia..."
                  onChange={filtros.handleChange}
                  values={filtros.values}
                  variant="gray-container"
                />
                <Input
                  id="descripcion"
                  title="Descripción"
                  placeholder="Buscar por descripción..."
                  onChange={filtros.handleChange}
                  values={filtros.values}
                  variant="gray-container"
                />
                <AsyncSelect
                  {...asyncFilterProps}
                  id="area_id"
                  title="Área"
                  placeholder="Todas las áreas"
                  getter={getArea}
                  optionsFormatConfig={{
                    value: "id",
                    label: (el: AreaI) => `${el.nombre}`,
                  }}
                />
                <AsyncSelect
                  {...asyncFilterProps}
                  id="nivel_id"
                  title="Nivel"
                  placeholder="Todos los niveles"
                  getter={getNiveles}
                  optionsFormatConfig={{
                    value: "id",
                    label: (el: NivelI) => `${el.nombre}`,
                  }}
                />
                <AsyncSelect
                  {...asyncFilterProps}
                  id="ciclo_id"
                  title="Ciclo"
                  placeholder="Todos los ciclos"
                  getter={getCiclo}
                  optionsFormatConfig={{
                    value: "id",
                    label: (el: CicloI) => `${el.nombre}`,
                  }}
                />
                <Select
                  id="cuatrimestre"
                  title="Cursado"
                  options={CURSADO_FILTRO_OPTIONS}
                  onChange={filtros.handleChange}
                  values={filtros.values}
                  variant="gray-container"
                  isSearchable={false}
                />
                <AsyncSelect
                  {...asyncFilterProps}
                  id="tipo_espacio_id"
                  title="Tipo de espacio"
                  placeholder="Todos los tipos de espacio"
                  getter={getTipoEspacio}
                  optionsFormatConfig={{
                    value: "id",
                    label: (el: TipoEspacioI) => `${el.nombre}`,
                  }}
                />
                <Input
                  id="sub_tipo"
                  title="Sub tipo"
                  placeholder="Buscar por sub tipo..."
                  onChange={filtros.handleChange}
                  values={filtros.values}
                  variant="gray-container"
                />
              </div>
              <div className="d-flex flex-wrap gap-2 mt-4">
                <Button
                  variant="secondary"
                  className="flex-fill text-nowrap"
                  onClick={handleClearFilters}
                >
                  Limpiar filtros
                </Button>
                <Button className="flex-fill text-nowrap" type="submit">
                  Aplicar filtros
                </Button>
              </div>
            </Card>
          </Col>

          <Col lg={8} xl={9} className="mb-3">
            <Card>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Espacios curriculares</h5>
                  <p className="mb-0">
                    Se encontraron <b>{cantidad}</b> espacios curriculares
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setFieldValue("arr_espacios_id", [])}
                  disabled={values.arr_espacios_id.length == 0}
                  style={
                    values.arr_espacios_id.length == 0
                      ? {
                          opacity: 0.5,
                          pointerEvents: "none",
                          cursor: "not-allowed",
                          backgroundColor: "inherit",
                          borderColor: "#20465eff",
                          color: "#20465eff",
                        }
                      : undefined
                  }
                >
                  Limpiar selección
                </Button>
              </div>

              {isLoadingEspacios ? (
                <div className="d-flex justify-content-center w-full">
                  <Spinner />
                </div>
              ) : (
                <>
                  <Table
                    striped
                    bordered
                    responsive
                    className="table-container"
                  >
                    <thead>
                      <tr className="table-heading">
                        <th>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isPageSelected}
                            onChange={togglePage}
                            aria-label="Seleccionar todos los de la página"
                          />
                        </th>
                        <th>Nombre</th>
                        <th>Descripción</th>
                        <th>Área</th>
                        <th>Nivel</th>
                        <th>Ciclo</th>
                        <th>Cursado</th>
                        <th>Tipo espacio</th>
                        <th>Sub tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {espacios.length > 0 ? (
                        espacios.map((ctEl) => (
                          <tr
                            className="table-row"
                            key={ctEl.id}
                            onClick={() => toggleEspacio(ctEl.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isSelected(ctEl.id)}
                                readOnly
                                aria-label={`Seleccionar ${ctEl.nombre}`}
                              />
                            </td>
                            <td>{ctEl.nombre}</td>
                            <td>{ctEl.descripcion}</td>
                            <td>{ctEl.area?.nombre}</td>
                            <td>{ctEl.nivel?.nombre}</td>
                            <td>{ctEl.ciclo?.nombre ?? "No asignado"}</td>
                            <td>{labelCursado(ctEl.cuatrimestre)}</td>
                            <td>{ctEl.tipo_espacio?.nombre}</td>
                            <td>
                              {ctEl.tipo_espacio?.con_sub_tipo
                                ? ctEl.sub_tipo
                                : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center">
                            No hay espacios curriculares para los filtros
                            aplicados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <Pagination
                    totalItems={cantidad}
                    currentPage={page}
                    currentLimit={limit}
                    onChangePage={handleChangePagination}
                    showPaginationOnlyOnMoreThanPages={1}
                  />
                </>
              )}
            </Card>
          </Col>
        </Row>

        <div
          className="d-flex justify-content-between align-items-center bg-white border-top py-3 px-4"
          style={{ position: "sticky", bottom: 0, zIndex: 10 }}
        >
          <div>
            <b>{values.arr_espacios_id.length}</b> espacios curriculares
            seleccionados
            {touched.arr_espacios_id && errors.arr_espacios_id && (
              <p className="component-error-msg mb-0">
                Seleccioná al menos un espacio curricular.
              </p>
            )}
          </div>
          <div className="d-flex column-gap-3">
            <Button variant="secondary" size="lg" onClick={handleReturn}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => handleSubmit()}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Guardando..." : "Guardar caja curricular"}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
